export const REPORT_STATUS_OPTIONS = [
	{ label: "Initial review", value: "in_review" },
	{ label: "In progress", value: "in_progress" },
	{ label: "Final review", value: "final_review" },
	{ label: "Approved - Open", value: "ready_to_close" },
	{ label: "Closed", value: "closed" },
];

// The statuses a submitted report can be in, as REPORT_STATUS_OPTIONS names
// them.
export type ReportStatusKey =
	"in_review" | "in_progress" | "final_review" | "ready_to_close" | "closed";

export const DEFAULT_REPORT_STATUSES = [
	"in_review",
	"in_progress",
	"final_review",
	"ready_to_close",
];

export const CONTROL_CENTER_STATUS_OPTIONS = [
	{ label: "Draft", value: "draft" },
	...REPORT_STATUS_OPTIONS,
];

export const DEFAULT_CONTROL_CENTER_STATUSES = [
	"draft",
	...DEFAULT_REPORT_STATUSES,
];

// A report's status as one key: a closed report is closed, and an open one is
// at its stage, with changes or a revision asked for counting as in progress,
// as matchesReportStatus does. Anything else, such as a draft, has none.
export function getReportStatusKey(
	status?: string | null,
	openStatus?: string | null
): ReportStatusKey | undefined {
	const normalizedStatus = status?.toLowerCase();
	if (normalizedStatus === "closed") return "closed";
	if (normalizedStatus !== "open") return undefined;
	switch (openStatus?.toLowerCase()) {
		case "in_review":
			return "in_review";
		case "in_progress":
		case "changes_required":
		case "revision_required":
			return "in_progress";
		case "final_review":
			return "final_review";
		case "ready_to_close":
			return "ready_to_close";
		default:
			return undefined;
	}
}

// What the streams call a status: "Initial review", "Closed" and so on.
export const getReportStatusLabel = (key: ReportStatusKey) =>
	REPORT_STATUS_OPTIONS.find((option) => option.value === key)?.label ?? key;

export function matchesReportStatus(
	status: string | undefined,
	selectedStatuses: string[]
): boolean {
	const normalizedStatus = status?.toLowerCase();
	return selectedStatuses.some((status) => {
		switch (status) {
			case "in_review":
				return normalizedStatus === "in_review";
			case "in_progress":
				return [
					"in_progress",
					"changes_required",
					"revision_required",
				].includes(normalizedStatus ?? "");
			case "final_review":
				return normalizedStatus === "final_review";
			case "ready_to_close":
				return normalizedStatus === "ready_to_close";
			case "closed":
				return normalizedStatus === "closed";
			default:
				return false;
		}
	});
}
